import React, { useEffect, useState, useCallback } from 'react'
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Modal,
  TextInput,
  Switch,
  Platform,
  Alert } from 'react-native'
import { ArrowLeft, Map as MapIcon, Pencil, ChevronDown, ChevronRight, ChevronLeft, X, Calendar } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'SpaceBrowse'>
  route: RouteProp<RootStackParamList, 'SpaceBrowse'>
}

interface Floor {
  id: string
  name: string
}

interface SpotType {
  id: string
  name: string
  width_cm: number
  depth_cm: number
  color: string
  price_per_day: number | null
  price_per_week: number | null
  price_per_month: number | null
}

interface Reservation {
  id: string
  start_date: string
  end_date: string
  status: string
  seller?: {
    id: string
    full_name: string | null
    email: string
  }
}

interface Spot {
  id: string
  floor_id: string
  spot_type_id: string
  spot_number: string
  x_position: number
  y_position: number
  rotation: number
  is_available: boolean
  is_active: boolean
  notes: string | null
  spot_type?: SpotType
  floor?: Floor
  current_reservation?: Reservation | null
}

interface Market {
  id: string
  name: string
}

interface SpotTypeGroup {
  spotType: SpotType
  spots: Spot[]
  expanded: boolean
}

export default function SpaceBrowseScreen({ navigation, route }: Props) {
  const { marketId } = route.params
  const { t } = useLanguage()
  const { user } = useAuth()

  const [spotTypeGroups, setSpotTypeGroups] = useState<SpotTypeGroup[]>([])
  const [floors, setFloors] = useState<Floor[]>([])
  const [market, setMarket] = useState<Market | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)

  // Edit modal state
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editingSpot, setEditingSpot] = useState<Spot | null>(null)
  const [editForm, setEditForm] = useState({
    is_available: true,
    is_active: true,
    notes: '' })
  const [saving, setSaving] = useState(false)

  // Spot type edit modal
  const [typeEditModalVisible, setTypeEditModalVisible] = useState(false)
  const [editingSpotType, setEditingSpotType] = useState<SpotType | null>(null)
  const [typeEditForm, setTypeEditForm] = useState({
    name: '',
    price_per_day: '',
    price_per_week: '',
    price_per_month: '' })

  // Reservation modal for non-admins
  const [reserveModalVisible, setReserveModalVisible] = useState(false)
  const [reservingSpot, setReservingSpot] = useState<Spot | null>(null)
  const [reservationLoading, setReservationLoading] = useState(false)
  const [marketCommission, setMarketCommission] = useState<number>(15) // Default 15%

  // Calendar state
  const [startDate, setStartDate] = useState<Date>(new Date())
  const [endDate, setEndDate] = useState<Date>(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))
  const [selectingDate, setSelectingDate] = useState<'start' | 'end' | null>(null)
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date())

  useEffect(() => {
    fetchData()
  }, [marketId])

  const fetchData = async () => {
    try {
      console.log('[SpaceBrowse] Fetching data for market:', marketId)

      // Fetch market
      const { data: marketData } = await supabase
        .from('flea_markets')
        .select('id, name, commission_percentage')
        .eq('id', marketId)
        .single()

      if (marketData) {
        setMarket(marketData)
        if (marketData.commission_percentage) {
          setMarketCommission(marketData.commission_percentage)
        }
      }
      console.log('[SpaceBrowse] Market:', marketData?.name)

      // Check if user is admin/owner of this market
      if (user?.id) {
        const { data: staffData } = await supabase
          .from('flea_market_staff')
          .select('role')
          .eq('flea_market_id', marketId)
          .eq('user_id', user.id)
          .is('deactivated_at', null)
          .single()

        setIsAdmin(staffData?.role === 'admin' || staffData?.role === 'owner')
      } else {
        setIsAdmin(false)
      }

      // Fetch floors
      const { data: floorsData, error: floorsError } = await supabase
        .from('floors')
        .select('id, name')
        .eq('flea_market_id', marketId)
        .order('sort_order')

      if (floorsError) throw floorsError
      setFloors(floorsData || [])
      console.log('[SpaceBrowse] Floors found:', floorsData?.length)

      if (!floorsData || floorsData.length === 0) {
        setSpotTypeGroups([])
        setLoading(false)
        setRefreshing(false)
        return
      }

      const floorIds = floorsData.map(f => f.id)

      // Fetch ALL spots (not just available ones) with their types
      console.log('[SpaceBrowse] Fetching spots for floor IDs:', floorIds)
      const { data: spotsData, error: spotsError } = await supabase
        .from('spots')
        .select(`
          *,
          spot_type:spot_types(*),
          floor:floors(id, name)
        `)
        .in('floor_id', floorIds)
        .order('spot_number')

      if (spotsError) {
        console.error('[SpaceBrowse] Spots error:', spotsError)
        throw spotsError
      }

      console.log('[SpaceBrowse] Spots found:', spotsData?.length, spotsData)
      const allSpots = (spotsData || []) as Spot[]

      // Fetch current reservations for these spots
      if (allSpots.length > 0) {
        const spotIds = allSpots.map(s => s.id)
        const today = new Date().toISOString().split('T')[0]

        const { data: reservations } = await supabase
          .from('reservations')
          .select(`
            id,
            spot_id,
            start_date,
            end_date,
            status,
            seller:users(id, full_name, email)
          `)
          .in('spot_id', spotIds)
          .in('status', ['pending', 'confirmed'])
          .gte('end_date', today)

        // Attach current reservation to spots
        if (reservations) {
          const reservationMap = new Map<string, Reservation>()
          for (const res of reservations) {
            const existing = reservationMap.get(res.spot_id)
            // Keep the most recent/active reservation
            if (!existing || res.start_date <= today) {
              reservationMap.set(res.spot_id, {
                id: res.id,
                start_date: res.start_date,
                end_date: res.end_date,
                status: res.status,
                seller: (res.seller as any) || undefined })
            }
          }

          for (const spot of allSpots) {
            spot.current_reservation = reservationMap.get(spot.id) || null
          }
        }
      }

      // Group spots by type (filter out orphaned spots with underscore prefix)
      const typeMap = new Map<string, SpotTypeGroup>()
      for (const spot of allSpots) {
        if (!spot.spot_type) continue
        // Skip orphaned spots (underscore prefix from renumbering conflicts)
        if (spot.spot_number.startsWith('_')) continue
        const typeId = spot.spot_type.id
        if (!typeMap.has(typeId)) {
          typeMap.set(typeId, {
            spotType: spot.spot_type,
            spots: [],
            expanded: false })
        }
        typeMap.get(typeId)!.spots.push(spot)
      }

      // Sort spots within each group by spot_number
      for (const group of typeMap.values()) {
        group.spots.sort((a, b) => {
          const numA = parseInt(a.spot_number.replace(/\D/g, '')) || 0
          const numB = parseInt(b.spot_number.replace(/\D/g, '')) || 0
          return numA - numB
        })
      }

      setSpotTypeGroups(Array.from(typeMap.values()))
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const onRefresh = useCallback(() => {
    setRefreshing(true)
    fetchData()
  }, [marketId])

  const toggleGroup = (typeId: string) => {
    setSpotTypeGroups(groups =>
      groups.map(g =>
        g.spotType.id === typeId ? { ...g, expanded: !g.expanded } : g
      )
    )
  }

  const openEditModal = (spot: Spot) => {
    setEditingSpot(spot)
    setEditForm({
      is_available: spot.is_available,
      is_active: spot.is_active,
      notes: spot.notes || '' })
    setEditModalVisible(true)
  }

  const openReserveModal = (spot: Spot) => {
    setReservingSpot(spot)
    // Initialize dates - default to 1 week from today
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
    setStartDate(today)
    setEndDate(nextWeek)
    setCalendarMonth(today)
    setSelectingDate(null)
    setReserveModalVisible(true)
  }

  const handleSpotPress = (spot: Spot) => {
    if (isAdmin) {
      openEditModal(spot)
    } else {
      // For non-admins, only allow opening reserve modal for available spots
      const status = getSpotStatus(spot)
      if (status.label === 'Available') {
        openReserveModal(spot)
      } else {
        // Show info that spot is not available
        showAlert(
          t('spot_unavailable') || 'Spot Unavailable',
          t('spot_unavailable_message') || 'This spot is currently not available for reservation.'
        )
      }
    }
  }

  const openTypeEditModal = (spotType: SpotType) => {
    setEditingSpotType(spotType)
    setTypeEditForm({
      name: spotType.name,
      price_per_day: spotType.price_per_day?.toString() || '',
      price_per_week: spotType.price_per_week?.toString() || '',
      price_per_month: spotType.price_per_month?.toString() || '' })
    setTypeEditModalVisible(true)
  }

  const saveSpot = async () => {
    if (!editingSpot) return

    setSaving(true)
    try {
      const { error } = await supabase
        .from('spots')
        .update({
          is_available: editForm.is_available,
          is_active: editForm.is_active,
          notes: editForm.notes || null })
        .eq('id', editingSpot.id)

      if (error) throw error

      // Update local state
      setSpotTypeGroups(groups =>
        groups.map(g => ({
          ...g,
          spots: g.spots.map(s =>
            s.id === editingSpot.id
              ? { ...s, ...editForm }
              : s
          ) }))
      )

      setEditModalVisible(false)
    } catch (error: any) {
      console.error('Error saving spot:', error)
      showAlert(t('error') || 'Error', error.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const saveSpotType = async () => {
    if (!editingSpotType) return

    setSaving(true)
    try {
      const { error } = await supabase
        .from('spot_types')
        .update({
          name: typeEditForm.name,
          price_per_day: typeEditForm.price_per_day ? parseFloat(typeEditForm.price_per_day) : null,
          price_per_week: typeEditForm.price_per_week ? parseFloat(typeEditForm.price_per_week) : null,
          price_per_month: typeEditForm.price_per_month ? parseFloat(typeEditForm.price_per_month) : null })
        .eq('id', editingSpotType.id)

      if (error) throw error

      // Update local state
      setSpotTypeGroups(groups =>
        groups.map(g =>
          g.spotType.id === editingSpotType.id
            ? {
                ...g,
                spotType: {
                  ...g.spotType,
                  name: typeEditForm.name,
                  price_per_day: typeEditForm.price_per_day ? parseFloat(typeEditForm.price_per_day) : null,
                  price_per_week: typeEditForm.price_per_week ? parseFloat(typeEditForm.price_per_week) : null,
                  price_per_month: typeEditForm.price_per_month ? parseFloat(typeEditForm.price_per_month) : null } }
            : g
        )
      )

      setTypeEditModalVisible(false)
    } catch (error: any) {
      console.error('Error saving spot type:', error)
      showAlert(t('error') || 'Error', error.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}\n\n${message}`)
    } else {
      Alert.alert(title, message)
    }
  }

  const getSpotStatus = (spot: Spot) => {
    if (!spot.is_active) return { label: 'Inactive', color: '#9e9e9e', bg: '#f5f5f5' }
    if (!spot.is_available) return { label: 'Unavailable', color: '#ff9800', bg: '#fff3e0' }
    if (spot.current_reservation) {
      return { label: 'Reserved', color: '#f44336', bg: '#ffebee' }
    }
    return { label: 'Available', color: '#4caf50', bg: '#e8f5e9' }
  }

  const formatPrice = (spotType: SpotType) => {
    const prices = []
    if (spotType.price_per_day) prices.push(`€${spotType.price_per_day}/day`)
    if (spotType.price_per_week) prices.push(`€${spotType.price_per_week}/week`)
    if (spotType.price_per_month) prices.push(`€${spotType.price_per_month}/month`)
    return prices.length > 0 ? prices.join(' • ') : 'No pricing set'
  }

  const getGroupStats = (group: SpotTypeGroup) => {
    const total = group.spots.length
    const available = group.spots.filter(s => s.is_available && s.is_active && !s.current_reservation).length
    const reserved = group.spots.filter(s => s.current_reservation).length
    return { total, available, reserved }
  }

  // Calendar helpers
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }

  const formatDateShort = (date: Date) => {
    return date.toLocaleDateString(language === 'fi' ? 'fi-FI' : 'en-US', {
      day: 'numeric',
      month: 'short' })
  }

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString(language === 'fi' ? 'fi-FI' : 'en-US', {
      month: 'long',
      year: 'numeric' })
  }

  const isSameDay = (d1: Date, d2: Date) => {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate()
  }

  const isInRange = (date: Date) => {
    return date >= startDate && date <= endDate
  }

  const isBeforeToday = (date: Date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }

  const handleDateSelect = (day: number) => {
    const selectedDate = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day)
    selectedDate.setHours(0, 0, 0, 0)

    if (isBeforeToday(selectedDate)) return

    if (selectingDate === 'start') {
      setStartDate(selectedDate)
      if (selectedDate > endDate) {
        setEndDate(selectedDate)
      }
      setSelectingDate('end')
    } else if (selectingDate === 'end') {
      if (selectedDate < startDate) {
        setStartDate(selectedDate)
      } else {
        setEndDate(selectedDate)
      }
      setSelectingDate(null)
    } else {
      // No specific selection, start fresh
      setStartDate(selectedDate)
      setSelectingDate('end')
    }
  }

  const calculateReservationDays = () => {
    const diffTime = endDate.getTime() - startDate.getTime()
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
  }

  const calculatePrice = () => {
    if (!reservingSpot?.spot_type) return 0
    const days = calculateReservationDays()
    const pricePerDay = reservingSpot.spot_type.price_per_day || 0
    return days * pricePerDay
  }

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(calendarMonth)
    const firstDay = getFirstDayOfMonth(calendarMonth)
    const days: (number | null)[] = []

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null)
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i)
    }

    const weeks: (number | null)[][] = []
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7))
    }

    const weekDays = language === 'fi'
      ? ['Su', 'Ma', 'Ti', 'Ke', 'To', 'Pe', 'La']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    return (
      <View style={styles.calendar}>
        {/* Month navigation */}
        <View style={styles.calendarHeader}>
          <TouchableOpacity
            onPress={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
            style={styles.calendarNavButton}
          >
            <ChevronLeft size={20} color={theme.colors.brand.primary} />
          </TouchableOpacity>
          <Text style={styles.calendarMonthText}>{formatMonthYear(calendarMonth)}</Text>
          <TouchableOpacity
            onPress={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
            style={styles.calendarNavButton}
          >
            <ChevronRight size={20} color={theme.colors.brand.primary} />
          </TouchableOpacity>
        </View>

        {/* Weekday headers */}
        <View style={styles.calendarWeekHeader}>
          {weekDays.map((day, idx) => (
            <Text key={idx} style={styles.calendarWeekDay}>{day}</Text>
          ))}
        </View>

        {/* Calendar days */}
        {weeks.map((week, weekIdx) => (
          <View key={weekIdx} style={styles.calendarWeek}>
            {week.map((day, dayIdx) => {
              if (day === null) {
                return <View key={dayIdx} style={styles.calendarDayEmpty} />
              }

              const date = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day)
              date.setHours(0, 0, 0, 0)
              const isStart = isSameDay(date, startDate)
              const isEnd = isSameDay(date, endDate)
              const inRange = isInRange(date)
              const isPast = isBeforeToday(date)

              return (
                <TouchableOpacity
                  key={dayIdx}
                  style={[
                    styles.calendarDay,
                    inRange && styles.calendarDayInRange,
                    isStart && styles.calendarDayStart,
                    isEnd && styles.calendarDayEnd,
                    isPast && styles.calendarDayDisabled,
                  ]}
                  onPress={() => handleDateSelect(day)}
                  disabled={isPast}
                >
                  <Text style={[
                    styles.calendarDayText,
                    (isStart || isEnd) && styles.calendarDayTextSelected,
                    isPast && styles.calendarDayTextDisabled,
                  ]}>
                    {day}
                  </Text>
                </TouchableOpacity>
              )
            })}
          </View>
        ))}
      </View>
    )
  }

  if (loading) {
    return (
      <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
    )
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Button variant="ghost" size="sm" onPress={() => navigation.goBack()}><ArrowLeft size={20} color="#748C70" /></Button>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>{t('browse_spaces') || 'Browse Spaces'}</Text>
          {market && <Text style={styles.headerSubtitle}>{market.name}</Text>}
        </View>
        {isAdmin && (
          <TouchableOpacity
            style={styles.mapViewButton}
            onPress={() => navigation.navigate('FloorPlanEditor', { marketId })}
          >
            <MapIcon size={20} color="#4caf50" />
          </TouchableOpacity>
        )}
      </View>

      {/* Summary bar */}
      <View style={styles.summaryBar}>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryNumber}>
            {spotTypeGroups.reduce((sum, g) => sum + g.spots.length, 0)}
          </Text>
          <Text style={styles.summaryLabel}>{t('total') || 'Total'}</Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={[styles.summaryNumber, { color: '#4caf50' }]}>
            {spotTypeGroups.reduce((sum, g) => sum + getGroupStats(g).available, 0)}
          </Text>
          <Text style={styles.summaryLabel}>{t('available') || 'Available'}</Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={[styles.summaryNumber, { color: '#f44336' }]}>
            {spotTypeGroups.reduce((sum, g) => sum + getGroupStats(g).reserved, 0)}
          </Text>
          <Text style={styles.summaryLabel}>{t('reserved') || 'Reserved'}</Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryNumber}>{spotTypeGroups.length}</Text>
          <Text style={styles.summaryLabel}>{t('types') || 'Types'}</Text>
        </View>
      </View>

      {spotTypeGroups.length === 0 ? (
        <View style={styles.emptyContainer}>
          <MapIcon size={64} color="#999" />
          <Text style={styles.emptyText}>
            {t('no_spots_available') || 'No spots available yet'}
          </Text>
          {isAdmin ? (
            <>
              <Text style={styles.emptySubtext}>
                {t('create_spots_hint') || 'Use the Floor Plan Editor to add spots to your market'}
              </Text>
              <TouchableOpacity
                style={styles.emptyButton}
                onPress={() => navigation.navigate('FloorPlanEditor', { marketId })}
              >
                <Text style={styles.emptyButtonText}>
                  {t('open_floor_plan') || 'Open Floor Plan Editor'}
                </Text>
              </TouchableOpacity>
            </>
          ) : (
            <Text style={styles.emptySubtext}>
              {t('check_back_later') || 'Check back later for available spots'}
            </Text>
          )}
        </View>
      ) : (
        <ScrollView
          style={styles.scrollView}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[theme.colors.brand.primary]}
            />
          }
        >
          {spotTypeGroups.map(group => {
            const stats = getGroupStats(group)
            return (
              <View key={group.spotType.id} style={styles.groupContainer}>
                {/* Group Header - Collapsible */}
                <TouchableOpacity
                  style={styles.groupHeader}
                  onPress={() => toggleGroup(group.spotType.id)}
                  activeOpacity={0.7}
                >
                  <View style={[styles.typeColorBadge, { backgroundColor: group.spotType.color }]} />
                  <View style={styles.groupHeaderInfo}>
                    <Text style={styles.groupName}>{group.spotType.name}</Text>
                    <Text style={styles.groupMeta}>
                      {group.spotType.width_cm} × {group.spotType.depth_cm} cm • {formatPrice(group.spotType)}
                    </Text>
                  </View>
                  <View style={styles.groupStats}>
                    <Text style={styles.groupStatsText}>
                      <Text style={{ color: '#4caf50' }}>{stats.available}</Text>
                      {' / '}
                      {stats.total}
                    </Text>
                  </View>
                  {isAdmin && (
                    <TouchableOpacity
                      style={styles.editTypeButton}
                      onPress={(e) => {
                        e.stopPropagation()
                        openTypeEditModal(group.spotType)
                      }}
                    >
                      <Pencil size={16} color="#666" />
                    </TouchableOpacity>
                  )}
                  {group.expanded ? (
                    <ChevronDown size={16} color="#999" />
                  ) : (
                    <ChevronRight size={16} color="#999" />
                  )}
                </TouchableOpacity>

                {/* Expanded Spots List */}
                {group.expanded && (
                  <View style={styles.spotsContainer}>
                    {group.spots.map(spot => {
                      const status = getSpotStatus(spot)
                      return (
                        <TouchableOpacity
                          key={spot.id}
                          style={styles.spotRow}
                          onPress={() => handleSpotPress(spot)}
                          activeOpacity={0.7}
                        >
                          <View style={styles.spotInfo}>
                            <Text style={styles.spotNumber}>{spot.spot_number}</Text>
                            {spot.floor && (
                              <Text style={styles.spotFloor}>{spot.floor.name}</Text>
                            )}
                          </View>

                          <View style={[styles.statusBadge, { backgroundColor: status.bg }]}>
                            <Text style={[styles.statusText, { color: status.color }]}>
                              {status.label}
                            </Text>
                          </View>

                          {spot.current_reservation?.seller && (
                            <View style={styles.sellerInfo}>
                              <Text style={styles.sellerName} numberOfLines={1}>
                                {spot.current_reservation.seller.full_name || spot.current_reservation.seller.email}
                              </Text>
                              <Text style={styles.reservationDates}>
                                {spot.current_reservation.start_date} - {spot.current_reservation.end_date}
                              </Text>
                            </View>
                          )}

                          <ChevronRight size={20} color="#ccc" />
                        </TouchableOpacity>
                      )
                    })}
                  </View>
                )}
              </View>
            )
          })}
          <View style={{ height: 40 }} />
        </ScrollView>
      )}

      {/* Spot Edit Modal */}
      <Modal
        visible={editModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setEditModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {t('edit_spot') || 'Edit Spot'} - {editingSpot?.spot_number}
              </Text>
              <TouchableOpacity onPress={() => setEditModalVisible(false)}>
                <X size={24} color="#999" />
              </TouchableOpacity>
            </View>

            {editingSpot && (
              <ScrollView>
                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>{t('spot_type') || 'Type'}</Text>
                  <Text style={styles.formValue}>{editingSpot.spot_type?.name}</Text>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>{t('floor') || 'Floor'}</Text>
                  <Text style={styles.formValue}>{editingSpot.floor?.name}</Text>
                </View>

                <View style={styles.switchRow}>
                  <Text style={styles.formLabel}>{t('is_active') || 'Active'}</Text>
                  <Switch
                    value={editForm.is_active}
                    onValueChange={(v) => setEditForm({ ...editForm, is_active: v })}
                    trackColor={{ false: '#ddd', true: theme.colors.brand.primary }}
                  />
                </View>

                <View style={styles.switchRow}>
                  <Text style={styles.formLabel}>{t('is_available') || 'Available for Booking'}</Text>
                  <Switch
                    value={editForm.is_available}
                    onValueChange={(v) => setEditForm({ ...editForm, is_available: v })}
                    trackColor={{ false: '#ddd', true: theme.colors.brand.primary }}
                  />
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>{t('notes') || 'Notes'}</Text>
                  <TextInput
                    style={styles.textArea}
                    value={editForm.notes}
                    onChangeText={(v) => setEditForm({ ...editForm, notes: v })}
                    multiline
                    numberOfLines={3}
                    placeholder={t('notes_placeholder') || 'Add notes about this spot...'}
                    placeholderTextColor="#999"
                  />
                </View>

                {editingSpot.current_reservation && (
                  <View style={styles.reservationSection}>
                    <Text style={styles.sectionTitle}>{t('current_reservation') || 'Current Reservation'}</Text>
                    <View style={styles.reservationCard}>
                      <Text style={styles.reservationSeller}>
                        {editingSpot.current_reservation.seller?.full_name || editingSpot.current_reservation.seller?.email}
                      </Text>
                      <Text style={styles.reservationDatesLarge}>
                        {editingSpot.current_reservation.start_date} → {editingSpot.current_reservation.end_date}
                      </Text>
                      <View style={[styles.statusBadge, { backgroundColor: '#e3f2fd', alignSelf: 'flex-start' }]}>
                        <Text style={[styles.statusText, { color: '#1976d2' }]}>
                          {editingSpot.current_reservation.status}
                        </Text>
                      </View>
                    </View>
                  </View>
                )}

                <TouchableOpacity
                  style={[styles.saveButton, saving && styles.saveButtonDisabled]}
                  onPress={saveSpot}
                  disabled={saving}
                >
                  {saving ? (
                    <Spinner color="#fff" />
                  ) : (
                    <Text style={styles.saveButtonText}>{t('save') || 'Save'}</Text>
                  )}
                </TouchableOpacity>
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* Spot Type Edit Modal */}
      <Modal
        visible={typeEditModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setTypeEditModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {t('edit_spot_type') || 'Edit Spot Type'}
              </Text>
              <TouchableOpacity onPress={() => setTypeEditModalVisible(false)}>
                <X size={24} color="#999" />
              </TouchableOpacity>
            </View>

            {editingSpotType && (
              <ScrollView>
                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>{t('name') || 'Name'}</Text>
                  <TextInput
                    style={styles.input}
                    value={typeEditForm.name}
                    onChangeText={(v) => setTypeEditForm({ ...typeEditForm, name: v })}
                    placeholder={t('type_name_placeholder') || 'e.g. Small Table'}
                    placeholderTextColor="#999"
                  />
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>{t('dimensions') || 'Dimensions'}</Text>
                  <Text style={styles.formValue}>
                    {editingSpotType.width_cm} × {editingSpotType.depth_cm} cm
                  </Text>
                  <Text style={styles.formHint}>
                    {t('edit_dimensions_hint') || 'Dimensions can be changed in the Floor Plan Editor'}
                  </Text>
                </View>

                <Text style={styles.sectionTitle}>{t('pricing') || 'Pricing'}</Text>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>{t('price_per_day') || 'Price per Day'}</Text>
                  <View style={styles.priceInputRow}>
                    <Text style={styles.currencySymbol}>€</Text>
                    <TextInput
                      style={styles.priceInput}
                      value={typeEditForm.price_per_day}
                      onChangeText={(v) => setTypeEditForm({ ...typeEditForm, price_per_day: v })}
                      keyboardType="decimal-pad"
                      placeholder="0.00"
                      placeholderTextColor="#999"
                    />
                  </View>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>{t('price_per_week') || 'Price per Week'}</Text>
                  <View style={styles.priceInputRow}>
                    <Text style={styles.currencySymbol}>€</Text>
                    <TextInput
                      style={styles.priceInput}
                      value={typeEditForm.price_per_week}
                      onChangeText={(v) => setTypeEditForm({ ...typeEditForm, price_per_week: v })}
                      keyboardType="decimal-pad"
                      placeholder="0.00"
                      placeholderTextColor="#999"
                    />
                  </View>
                </View>

                <View style={styles.formGroup}>
                  <Text style={styles.formLabel}>{t('price_per_month') || 'Price per Month'}</Text>
                  <View style={styles.priceInputRow}>
                    <Text style={styles.currencySymbol}>€</Text>
                    <TextInput
                      style={styles.priceInput}
                      value={typeEditForm.price_per_month}
                      onChangeText={(v) => setTypeEditForm({ ...typeEditForm, price_per_month: v })}
                      keyboardType="decimal-pad"
                      placeholder="0.00"
                      placeholderTextColor="#999"
                    />
                  </View>
                </View>

                <TouchableOpacity
                  style={[styles.saveButton, saving && styles.saveButtonDisabled]}
                  onPress={saveSpotType}
                  disabled={saving}
                >
                  {saving ? (
                    <Spinner color="#fff" />
                  ) : (
                    <Text style={styles.saveButtonText}>{t('save') || 'Save'}</Text>
                  )}
                </TouchableOpacity>
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* Reservation Modal for non-admins */}
      <Modal
        visible={reserveModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setReserveModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {t('reserve_spot') || 'Reserve Spot'} - {reservingSpot?.spot_number}
              </Text>
              <TouchableOpacity onPress={() => setReserveModalVisible(false)}>
                <X size={24} color="#999" />
              </TouchableOpacity>
            </View>

            {reservingSpot && (
              <ScrollView>
                {/* Spot info */}
                <View style={styles.spotInfoRow}>
                  <View style={styles.spotInfoItem}>
                    <Text style={styles.spotInfoLabel}>{t('spot_type') || 'Type'}</Text>
                    <Text style={styles.spotInfoValue}>{reservingSpot.spot_type?.name}</Text>
                  </View>
                  <View style={styles.spotInfoItem}>
                    <Text style={styles.spotInfoLabel}>{t('dimensions') || 'Size'}</Text>
                    <Text style={styles.spotInfoValue}>
                      {reservingSpot.spot_type?.width_cm}×{reservingSpot.spot_type?.depth_cm}cm
                    </Text>
                  </View>
                  {reservingSpot.floor && (
                    <View style={styles.spotInfoItem}>
                      <Text style={styles.spotInfoLabel}>{t('floor') || 'Floor'}</Text>
                      <Text style={styles.spotInfoValue}>{reservingSpot.floor.name}</Text>
                    </View>
                  )}
                </View>

                {/* Date selection buttons */}
                <View style={styles.dateSelectionRow}>
                  <TouchableOpacity
                    style={[styles.dateButton, selectingDate === 'start' && styles.dateButtonActive]}
                    onPress={() => setSelectingDate('start')}
                  >
                    <Text style={styles.dateButtonLabel}>{t('start_date') || 'Start'}</Text>
                    <Text style={[styles.dateButtonValue, selectingDate === 'start' && styles.dateButtonValueActive]}>
                      {formatDateShort(startDate)}
                    </Text>
                  </TouchableOpacity>
                  <View style={styles.dateArrow}>
                    <ChevronRight size={20} color="#999" />
                  </View>
                  <TouchableOpacity
                    style={[styles.dateButton, selectingDate === 'end' && styles.dateButtonActive]}
                    onPress={() => setSelectingDate('end')}
                  >
                    <Text style={styles.dateButtonLabel}>{t('end_date') || 'End'}</Text>
                    <Text style={[styles.dateButtonValue, selectingDate === 'end' && styles.dateButtonValueActive]}>
                      {formatDateShort(endDate)}
                    </Text>
                  </TouchableOpacity>
                </View>

                {/* Calendar */}
                {renderCalendar()}

                {/* Price summary */}
                <View style={styles.priceSummary}>
                  <View style={styles.priceRow}>
                    <Text style={styles.priceRowLabel}>
                      {calculateReservationDays()} {calculateReservationDays() === 1 ? t('day') || 'day' : t('days') || 'days'} × €{reservingSpot.spot_type?.price_per_day || 0}
                    </Text>
                    <Text style={styles.priceRowValue}>€{calculatePrice().toFixed(2)}</Text>
                  </View>
                  <View style={styles.priceRow}>
                    <Text style={styles.priceRowLabel}>{t('commission_on_sales') || 'Commission on sales'}</Text>
                    <Text style={styles.priceRowValue}>{marketCommission}%</Text>
                  </View>
                  <View style={[styles.priceRow, styles.priceTotalRow]}>
                    <Text style={styles.priceTotalLabel}>{t('total_to_pay') || 'Total to pay'}</Text>
                    <Text style={styles.priceTotalValue}>€{calculatePrice().toFixed(2)}</Text>
                  </View>
                </View>

                <TouchableOpacity
                  style={[styles.saveButton, reservationLoading && styles.saveButtonDisabled]}
                  disabled={reservationLoading}
                  onPress={async () => {
                    if (!user?.id || !reservingSpot) return

                    setReservationLoading(true)
                    try {
                      // Use selected dates from calendar
                      const startDateStr = startDate.toISOString().split('T')[0]
                      const endDateStr = endDate.toISOString().split('T')[0]
                      const basePrice = calculatePrice()

                      // Create reservation - confirmed immediately (payment to follow)
                      const { data: reservation, error } = await supabase
                        .from('reservations')
                        .insert({
                          spot_id: reservingSpot.id,
                          seller_id: user.id,
                          flea_market_id: marketId,
                          start_date: startDateStr,
                          end_date: endDateStr,
                          base_price: basePrice,
                          commission_percentage: marketCommission,
                          status: 'confirmed' })
                        .select('id')
                        .single()

                      if (error) throw error

                      showAlert(
                        t('reservation_confirmed') || 'Reservation Confirmed!',
                        t('reservation_confirmed_message') || 'Your spot has been reserved. You can now add products to sell.'
                      )
                      setReserveModalVisible(false)
                      fetchData() // Refresh to show updated status

                      // TODO: Navigate to payment if needed
                      // navigation.navigate('Checkout', { reservationId: reservation.id })
                    } catch (error: any) {
                      console.error('Error creating reservation:', error)
                      showAlert(t('error') || 'Error', error.message || 'Failed to create reservation')
                    } finally {
                      setReservationLoading(false)
                    }
                  }}
                >
                  {reservationLoading ? (
                    <Spinner color="#fff" />
                  ) : (
                    <Text style={styles.saveButtonText}>{t('confirm_reservation') || 'Confirm Reservation'}</Text>
                  )}
                </TouchableOpacity>
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5' },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee' },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center' },
  backButtonText: {
    fontSize: 24,
    color: theme.colors.brand.primary },
  headerTitleContainer: {
    flex: 1,
    marginLeft: 8 },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333' },
  headerSubtitle: {
    fontSize: 14,
    color: '#666',
    marginTop: 2 },
  mapViewButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#e8f5e9',
    borderRadius: 20 },
  mapViewButtonText: {
    fontSize: 20 },
  summaryBar: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#eee' },
  summaryItem: {
    flex: 1,
    alignItems: 'center' },
  summaryNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333' },
  summaryLabel: {
    fontSize: 12,
    color: '#666',
    marginTop: 2 },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32 },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 16 },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    textAlign: 'center' },
  emptySubtext: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 24 },
  emptyButton: {
    backgroundColor: theme.colors.brand.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8 },
  emptyButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 16 },
  scrollView: {
    flex: 1 },
  groupContainer: {
    backgroundColor: '#fff',
    marginTop: 8,
    marginHorizontal: 8,
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2 },
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#fafafa' },
  typeColorBadge: {
    width: 12,
    height: 40,
    borderRadius: 6,
    marginRight: 12 },
  groupHeaderInfo: {
    flex: 1 },
  groupName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333' },
  groupMeta: {
    fontSize: 12,
    color: '#666',
    marginTop: 2 },
  groupStats: {
    marginRight: 8 },
  groupStatsText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333' },
  editTypeButton: {
    padding: 8,
    marginRight: 4 },
  editTypeButtonText: {
    fontSize: 16 },
  expandIcon: {
    fontSize: 12,
    color: '#999',
    width: 20,
    textAlign: 'center' },
  spotsContainer: {
    borderTopWidth: 1,
    borderTopColor: '#eee' },
  spotRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0' },
  spotInfo: {
    width: 80 },
  spotNumber: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333' },
  spotFloor: {
    fontSize: 11,
    color: '#999' },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 12 },
  statusText: {
    fontSize: 12,
    fontWeight: '500' },
  sellerInfo: {
    flex: 1,
    marginRight: 8 },
  sellerName: {
    fontSize: 14,
    color: '#333' },
  reservationDates: {
    fontSize: 11,
    color: '#666' },
  editIcon: {
    fontSize: 20,
    color: '#ccc' },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end' },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 24,
    maxHeight: '80%' },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20 },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333' },
  modalClose: {
    fontSize: 24,
    color: '#999',
    padding: 4 },
  formGroup: {
    marginBottom: 16 },
  formLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
    marginBottom: 6 },
  formValue: {
    fontSize: 16,
    color: '#333' },
  formHint: {
    fontSize: 12,
    color: '#999',
    marginTop: 4 },
  input: {
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: '#333' },
  textArea: {
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: '#333',
    minHeight: 80,
    textAlignVertical: 'top' },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingVertical: 8 },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginTop: 8,
    marginBottom: 12 },
  reservationSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#eee' },
  reservationCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12 },
  reservationSeller: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4 },
  reservationDatesLarge: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8 },
  priceInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    paddingHorizontal: 12 },
  currencySymbol: {
    fontSize: 16,
    color: '#666',
    marginRight: 4 },
  priceInput: {
    flex: 1,
    padding: 12,
    fontSize: 16,
    color: '#333' },
  saveButton: {
    backgroundColor: theme.colors.brand.primary,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 24 },
  saveButtonDisabled: {
    opacity: 0.6 },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600' },
  priceText: {
    fontSize: 16,
    color: '#333',
    marginBottom: 4 },
  noPricingText: {
    fontSize: 14,
    color: '#999',
    fontStyle: 'italic',
    marginTop: 8 },
  durationOptions: {
    flexDirection: 'row',
    marginTop: 8 },
  durationOption: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
    marginHorizontal: 4 },
  durationOptionSelected: {
    borderColor: theme.colors.brand.primary,
    backgroundColor: `${theme.colors.brand.primary}10` },
  durationPrice: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333' },
  durationLabel: {
    fontSize: 12,
    color: '#666',
    marginTop: 2 },
  durationTextSelected: {
    color: theme.colors.brand.primary },
  priceSummary: {
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    padding: 12,
    marginTop: 16 },
  priceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8 },
  priceRowLabel: {
    fontSize: 14,
    color: '#666' },
  priceRowValue: {
    fontSize: 14,
    color: '#333' },
  priceTotalRow: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#ddd',
    marginBottom: 0 },
  priceTotalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333' },
  priceTotalValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.brand.primary },
  // Spot info row in reservation modal
  spotInfoRow: {
    flexDirection: 'row',
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16 },
  spotInfoItem: {
    flex: 1,
    alignItems: 'center' },
  spotInfoLabel: {
    fontSize: 11,
    color: '#666',
    marginBottom: 2 },
  spotInfoValue: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333' },
  // Date selection
  dateSelectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12 },
  dateButton: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent' },
  dateButtonActive: {
    borderColor: theme.colors.brand.primary,
    backgroundColor: `${theme.colors.brand.primary}10` },
  dateButtonLabel: {
    fontSize: 11,
    color: '#666',
    marginBottom: 2 },
  dateButtonValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333' },
  dateButtonValueActive: {
    color: theme.colors.brand.primary },
  dateArrow: {
    paddingHorizontal: 8 },
  // Calendar
  calendar: {
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#eee',
    marginBottom: 16 },
  calendarHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee' },
  calendarNavButton: {
    padding: 4 },
  calendarMonthText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    textTransform: 'capitalize' },
  calendarWeekHeader: {
    flexDirection: 'row',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0' },
  calendarWeekDay: {
    flex: 1,
    textAlign: 'center',
    fontSize: 12,
    color: '#666',
    fontWeight: '500' },
  calendarWeek: {
    flexDirection: 'row' },
  calendarDay: {
    flex: 1,
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: 44 },
  calendarDayEmpty: {
    flex: 1,
    aspectRatio: 1,
    maxHeight: 44 },
  calendarDayInRange: {
    backgroundColor: `${theme.colors.brand.primary}15` },
  calendarDayStart: {
    backgroundColor: theme.colors.brand.primary,
    borderTopLeftRadius: 20,
    borderBottomLeftRadius: 20 },
  calendarDayEnd: {
    backgroundColor: theme.colors.brand.primary,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20 },
  calendarDayDisabled: {
    opacity: 0.3 },
  calendarDayText: {
    fontSize: 14,
    color: '#333' },
  calendarDayTextSelected: {
    color: '#fff',
    fontWeight: '600' },
  calendarDayTextDisabled: {
    color: '#999' } })
